/**
 * Automation Service
 * Pure functions for recurring-task automations.
 * No I/O: callers handle KV persistence and UI updates.
 */

const MAX_OCCURRENCES = 365;
const VALID_PATTERNS = ['daily', 'weekly', 'monthly', 'every-x-days'];
const VALID_TASK_START_MODES = ['none', 'creation-day'];
const VALID_TASK_END_MODES = ['none', 'creation-day', 'offset-days', 'offset-creation-days'];

export function toISODate(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}

function parseISODate(iso) {
    const [y, m, d] = String(iso).split('-').map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
}

function diffDays(startISO, endISO) {
    if (!startISO || !endISO) return null;
    const start = parseISODate(startISO);
    const end = parseISODate(endISO);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
    return Math.max(0, Math.round((end.getTime() - start.getTime()) / 86400000));
}

function isLastDayOfMonth(date) {
    const probe = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    return date.getDate() === probe.getDate();
}

function daysInMonth(year, monthIndex) {
    return new Date(year, monthIndex + 1, 0).getDate();
}

/**
 * Compute the next run date for an automation rule, starting from fromISO.
 * Returns an ISO date string (YYYY-MM-DD) or null if the rule is invalid.
 *
 * - daily: +1 day
 * - every-x-days: +intervalDays
 * - weekly: advances to the next matching day in daysOfWeek (0=Sun..6=Sat)
 * - monthly: +1 month; clamps to last day if the target month is shorter,
 *   or keeps last-day behavior if fromISO was on the last day of its month
 */
export function getNextRunDate(rule, fromISO) {
    if (!rule || !VALID_PATTERNS.includes(rule.pattern) || !fromISO) return null;

    const from = parseISODate(fromISO);
    if (isNaN(from.getTime())) return null;

    const d = new Date(from);

    switch (rule.pattern) {
        case 'daily':
            d.setDate(d.getDate() + 1);
            break;

        case 'every-x-days': {
            const step = parseInt(rule.intervalDays, 10);
            if (!Number.isFinite(step) || step < 1) return null;
            const unit = rule.intervalUnit || 'days';
            if (unit === 'months') {
                d.setMonth(d.getMonth() + step);
            } else if (unit === 'years') {
                d.setFullYear(d.getFullYear() + step);
            } else {
                d.setDate(d.getDate() + step);
            }
            break;
        }

        case 'weekly': {
            const days = Array.isArray(rule.daysOfWeek)
                ? rule.daysOfWeek.map(n => parseInt(n, 10)).filter(n => n >= 0 && n <= 6)
                : [];
            if (days.length === 0) return null;
            // Advance at least one day, then step until we hit a selected weekday.
            let guard = 0;
            do {
                d.setDate(d.getDate() + 1);
                guard++;
            } while (!days.includes(d.getDay()) && guard < 14);
            if (guard >= 14) return null;
            break;
        }

        case 'monthly': {
            const wantLastDay = isLastDayOfMonth(from);
            const targetMonth = from.getMonth() + 1;
            const targetYear = from.getFullYear() + Math.floor(targetMonth / 12);
            const normMonth = targetMonth % 12;
            const monthLen = daysInMonth(targetYear, normMonth);
            const day = wantLastDay ? monthLen : Math.min(from.getDate(), monthLen);
            d.setFullYear(targetYear, normMonth, day);
            break;
        }
    }

    return toISODate(d);
}

/**
 * Validate a rule draft before saving.
 * Returns { valid, errors[] }.
 */
export function validateRule(rule) {
    const errors = [];
    if (!rule || typeof rule !== 'object') {
        return { valid: false, errors: ['Rule is required'] };
    }
    const todayISO = toISODate(new Date());
    if (!VALID_PATTERNS.includes(rule.pattern)) {
        errors.push('Invalid pattern');
    }
    if (rule.pattern === 'weekly') {
        const days = Array.isArray(rule.daysOfWeek) ? rule.daysOfWeek : [];
        const valid = days.every(n => Number.isInteger(n) && n >= 0 && n <= 6);
        if (!valid || days.length === 0) {
            errors.push('Select at least one weekday');
        }
    }
    if (rule.pattern === 'every-x-days') {
        const n = parseInt(rule.intervalDays, 10);
        if (!Number.isFinite(n) || n < 1) {
            errors.push('Interval must be a positive integer');
        }
    }
    if (rule.endDate && !/^\d{4}-\d{2}-\d{2}$/.test(rule.endDate)) {
        errors.push('End date must be YYYY-MM-DD');
    }
    if (rule.firstOccurrenceDate && !/^\d{4}-\d{2}-\d{2}$/.test(rule.firstOccurrenceDate)) {
        errors.push('First occurrence must be YYYY-MM-DD');
    }
    if (rule.firstOccurrenceDate && rule.firstOccurrenceDate < todayISO) {
        errors.push('First occurrence cannot be in the past');
    }
    if (!VALID_TASK_START_MODES.includes(rule.taskStartDateMode)) {
        errors.push('Start date mode is required');
    }
    if (!VALID_TASK_END_MODES.includes(rule.taskEndDateMode)) {
        errors.push('End date mode is required');
    }
    if (rule.taskEndDateMode === 'offset-days') {
        const offset = parseInt(rule.taskEndDateOffset, 10);
        if (!Number.isFinite(offset) || offset < 1) {
            errors.push('End date offset must be a positive integer');
        }
    }
    if (!rule.template || !rule.template.title) {
        errors.push('Template title is required');
    }
    return { valid: errors.length === 0, errors };
}

/**
 * Build a rule object from a source task and a draft config.
 */
export function buildRuleFromTask(task, draft, ruleCounter) {
    if (!task) return null;
    const startISO = (draft.firstRunDate || task.startDate || toISODate(new Date())).slice(0, 10);

    return {
        id: ruleCounter,
        type: 'recurring-task',
        active: true,
        name: (draft.name || '').trim() || null,
        template: {
            title: task.title || '',
            description: task.description || '',
            projectId: task.projectId ?? null,
            priority: task.priority || 'medium',
            tags: Array.isArray(task.tags) ? [...task.tags] : [],
            durationDays: diffDays(task.startDate, task.endDate),
        },
        pattern: draft.pattern,
        daysOfWeek: draft.pattern === 'weekly' ? [...(draft.daysOfWeek || [])] : null,
        intervalDays: draft.pattern === 'every-x-days' ? parseInt(draft.intervalDays, 10) : null,
        intervalUnit: draft.pattern === 'every-x-days' ? (draft.intervalUnit || 'days') : null,
        taskStartDateMode: draft.taskStartDateMode || 'creation-day',
        taskEndDateMode: draft.taskEndDateMode || 'none',
        taskEndDateOffset: draft.taskEndDateMode === 'offset-days'
            ? Math.max(1, parseInt(draft.taskEndDateOffset, 10) || 1)
            : null,
        firstOccurrenceDate: draft.firstOccurrenceDate || startISO,
        nextRunDate: startISO,
        endDate: draft.endDate || null,
        occurrenceCount: 0,
        maxOccurrences: MAX_OCCURRENCES,
        sourceTaskId: task.id ?? null,
        createdAt: new Date().toISOString(),
    };
}

/**
 * Materialize a task instance from a rule's template.
 * The caller is responsible for assigning the task id and persisting.
 */
export function buildTaskFromRule(rule, runISO) {
    if (!rule || !rule.template) return null;
    const startDateMode = rule.taskStartDateMode || 'creation-day';
    const startDate = startDateMode === 'none' ? '' : runISO;

    let endDate = '';
    const endDateMode = rule.taskEndDateMode || 'none';
    if (endDateMode === 'creation-day') {
        endDate = runISO;
    } else if (endDateMode === 'offset-days') {
        const offset = Math.max(1, parseInt(rule.taskEndDateOffset, 10) || 1);
        const base = startDateMode === 'creation-day'
            ? parseISODate(startDate || runISO)
            : parseISODate(runISO);
        base.setDate(base.getDate() + offset);
        endDate = toISODate(base);
    }

    return {
        title: rule.template.title,
        description: rule.template.description || '',
        projectId: rule.template.projectId ?? null,
        priority: rule.template.priority || 'medium',
        status: 'todo',
        tags: Array.isArray(rule.template.tags) ? [...rule.template.tags] : [],
        startDate,
        endDate,
        automationId: rule.id,
    };
}

/**
 * Decide whether a rule should generate an instance today.
 * todayISO is the comparator (UTC or user-tz computed by caller).
 */
export function ruleShouldFire(rule, todayISO) {
    if (!rule || !rule.active) return false;
    if (!rule.nextRunDate) return false;
    if (rule.nextRunDate > todayISO) return false;
    if (rule.endDate && rule.nextRunDate > rule.endDate) return false;
    if (typeof rule.occurrenceCount === 'number'
        && typeof rule.maxOccurrences === 'number'
        && rule.occurrenceCount >= rule.maxOccurrences) {
        return false;
    }
    return true;
}

/**
 * Apply a single firing: returns the updated rule (immutable) with
 * occurrenceCount++, next nextRunDate, and active=false if limits were hit.
 */
export function advanceRule(rule) {
    if (!rule) return rule;
    const nextCount = (rule.occurrenceCount || 0) + 1;
    const nextDate = getNextRunDate(rule, rule.nextRunDate);
    const hitMax = typeof rule.maxOccurrences === 'number' && nextCount >= rule.maxOccurrences;
    const pastEnd = rule.endDate && nextDate && nextDate > rule.endDate;
    const stillActive = !!nextDate && !hitMax && !pastEnd;
    return {
        ...rule,
        occurrenceCount: nextCount,
        nextRunDate: nextDate || rule.nextRunDate,
        active: stillActive ? rule.active : false,
    };
}

/**
 * Human-readable summary of a rule (no i18n — caller may localize).
 */
export function describeRule(rule) {
    if (!rule) return '';
    switch (rule.pattern) {
        case 'daily':
            return 'Daily';
        case 'every-x-days':
            return `Every ${rule.intervalDays} days`;
        case 'weekly': {
            const names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            const sorted = [...(rule.daysOfWeek || [])].sort((a, b) => a - b);
            return `Weekly · ${sorted.map(n => names[n]).join(', ')}`;
        }
        case 'monthly':
            return 'Monthly';
        default:
            return rule.pattern || '';
    }
}

// ─────────────────────────────────────────────────────────────────────────────
// === Auto-archive automations ===
// Pure helpers for the `auto-archive` automation kind. Three trigger modes:
//   done-age — archive Done tasks that have been Done for >= thresholdDays
//   on-date  — one-shot: archive every Done task on a specific calendar date
//   periodic — recurring daily/weekly/monthly cleanup of Done tasks
// No I/O: callers handle persistence and UI updates.
// ─────────────────────────────────────────────────────────────────────────────

const VALID_ARCHIVE_MODES = ['done-age', 'on-date', 'periodic'];
const VALID_ARCHIVE_PATTERNS = ['daily', 'weekly', 'monthly'];
const MAX_THRESHOLD_DAYS = 3650;

/**
 * Next run date for a periodic auto-archive rule, called AFTER a rule has run.
 * options.daysOfWeek (0=Sun…6=Sat) — for weekly, finds the closest next matching day.
 * options.dayOfMonth (1-31, or 0 = last day) — for monthly, clamps to the month's actual length.
 */
export function getNextArchiveRunDate(pattern, fromISO, options = {}) {
    if (!VALID_ARCHIVE_PATTERNS.includes(pattern) || !fromISO) return null;
    const from = parseISODate(fromISO);
    if (isNaN(from.getTime())) return null;

    if (pattern === 'daily') {
        const d = new Date(from);
        d.setDate(d.getDate() + 1);
        return toISODate(d);
    }

    if (pattern === 'weekly') {
        const days = Array.isArray(options.daysOfWeek) && options.daysOfWeek.length > 0
            ? [...new Set(options.daysOfWeek)].sort((a, b) => a - b)
            : null;
        if (days) {
            for (let i = 1; i <= 7; i++) {
                const candidate = new Date(from);
                candidate.setDate(from.getDate() + i);
                if (days.includes(candidate.getDay())) return toISODate(candidate);
            }
        }
        const d = new Date(from);
        d.setDate(d.getDate() + 7);
        return toISODate(d);
    }

    // monthly
    const targetMonth = from.getMonth() + 1;
    const targetYear = from.getFullYear() + Math.floor(targetMonth / 12);
    const normMonth = targetMonth % 12;
    const monthLen = daysInMonth(targetYear, normMonth);
    const dom = options.dayOfMonth;
    const targetDay = dom === 0 ? monthLen
        : (Number.isFinite(dom) && dom >= 1) ? Math.min(dom, monthLen)
        : Math.min(from.getDate(), monthLen);
    const d = new Date(from);
    d.setFullYear(targetYear, normMonth, targetDay);
    return toISODate(d);
}

/**
 * First run date for a NEW periodic rule — on or after todayISO.
 * Anchors weekly to the chosen day(s) and monthly to the chosen day-of-month.
 */
function firstArchiveRunDate(pattern, todayISO, options = {}) {
    if (!VALID_ARCHIVE_PATTERNS.includes(pattern) || !todayISO) return todayISO;
    const today = parseISODate(todayISO);
    if (isNaN(today.getTime())) return todayISO;

    if (pattern === 'daily') return todayISO;

    if (pattern === 'weekly') {
        const days = Array.isArray(options.daysOfWeek) && options.daysOfWeek.length > 0
            ? [...new Set(options.daysOfWeek)].sort((a, b) => a - b)
            : null;
        if (days) {
            for (let i = 0; i <= 6; i++) {
                const candidate = new Date(today);
                candidate.setDate(today.getDate() + i);
                if (days.includes(candidate.getDay())) return toISODate(candidate);
            }
        }
        return todayISO;
    }

    // monthly
    const dom = options.dayOfMonth;
    if (!Number.isFinite(dom) && dom !== 0) return todayISO;
    const thisMonthLen = daysInMonth(today.getFullYear(), today.getMonth());
    const targetDay = dom === 0 ? thisMonthLen : Math.min(dom, thisMonthLen);
    const thisMonthISO = toISODate(new Date(today.getFullYear(), today.getMonth(), targetDay));
    if (thisMonthISO >= todayISO) return thisMonthISO;
    // This month's date has passed — use next month
    const nm = today.getMonth() + 1;
    const ny = today.getFullYear() + Math.floor(nm / 12);
    const normNm = nm % 12;
    const nextLen = daysInMonth(ny, normNm);
    return toISODate(new Date(ny, normNm, Math.min(targetDay, nextLen)));
}

/**
 * Validate an auto-archive rule draft before saving.
 * Returns { valid, errors[] }.
 */
export function validateArchiveRule(rule) {
    const errors = [];
    if (!rule || typeof rule !== 'object') {
        return { valid: false, errors: ['Rule is required'] };
    }
    if (!VALID_ARCHIVE_MODES.includes(rule.mode)) {
        return { valid: false, errors: ['Invalid trigger mode'] };
    }
    if (rule.mode === 'done-age') {
        const n = parseInt(rule.thresholdDays, 10);
        if (!Number.isFinite(n) || n < 1 || n > MAX_THRESHOLD_DAYS) {
            errors.push('Threshold must be between 1 and 3650 days');
        }
    }
    if (rule.mode === 'on-date') {
        if (!rule.archiveDate || !/^\d{4}-\d{2}-\d{2}$/.test(rule.archiveDate)) {
            errors.push('Choose a valid archive date');
        }
    }
    if (rule.mode === 'periodic') {
        if (!VALID_ARCHIVE_PATTERNS.includes(rule.pattern)) {
            errors.push('Choose a cleanup frequency');
        }
    }
    return { valid: errors.length === 0, errors };
}

/**
 * Build a normalized auto-archive rule from a draft config.
 * Only the fields relevant to the chosen mode are populated.
 */
export function buildArchiveRule(draft, ruleId) {
    if (!draft || !VALID_ARCHIVE_MODES.includes(draft.mode)) return null;
    const todayISO = toISODate(new Date());
    const rule = {
        id: ruleId,
        type: 'auto-archive',
        active: draft.active !== false,
        mode: draft.mode,
        thresholdDays: null,
        archiveDate: null,
        pattern: null,
        nextRunDate: null,
        lastRunDate: draft.lastRunDate || null,
        archivedCount: Number.isFinite(draft.archivedCount) ? draft.archivedCount : 0,
        // Rules are intentionally conservative: without an explicit opt-in,
        // only standalone tasks may be auto-archived.
        includeProjectTasks: draft.includeProjectTasks === true,
        createdAt: draft.createdAt || new Date().toISOString(),
    };

    if (draft.mode === 'done-age') {
        rule.thresholdDays = Math.max(1, Math.min(
            MAX_THRESHOLD_DAYS,
            parseInt(draft.thresholdDays, 10) || 1
        ));
    } else if (draft.mode === 'on-date') {
        rule.archiveDate = draft.archiveDate;
        rule.nextRunDate = draft.archiveDate;
    } else if (draft.mode === 'periodic') {
        rule.pattern = draft.pattern;
        rule.daysOfWeek = Array.isArray(draft.daysOfWeek) && draft.daysOfWeek.length > 0
            ? [...draft.daysOfWeek] : null;
        rule.dayOfMonth = Number.isFinite(draft.dayOfMonth) ? draft.dayOfMonth : null;
        const opts = { daysOfWeek: rule.daysOfWeek, dayOfMonth: rule.dayOfMonth };
        rule.nextRunDate = draft.nextRunDate
            || firstArchiveRunDate(draft.pattern, todayISO, opts);
    }
    return rule;
}

/**
 * Whether an on-date/periodic rule is due to run on todayISO.
 * done-age rules are continuous and never gated by a date.
 */
export function archiveRuleShouldRun(rule, todayISO) {
    if (!rule || !rule.active || !todayISO) return false;
    if (rule.mode === 'on-date') {
        return !!rule.archiveDate && rule.archiveDate <= todayISO;
    }
    if (rule.mode === 'periodic') {
        return !!rule.nextRunDate && rule.nextRunDate <= todayISO;
    }
    return false;
}

/**
 * Resolve the timestamp (ms) a task entered Done.
 * Prefers completedDate; falls back so legacy Done tasks are still measurable.
 */
export function getDoneSinceMs(task) {
    const raw = task && (task.completedDate || task.completedAt || task.updatedAt || task.createdAt);
    if (!raw) return null;
    const ms = new Date(raw).getTime();
    return Number.isFinite(ms) ? ms : null;
}

/**
 * Whether a single task is eligible for auto-archiving under a rule.
 * ctx = { nowMs }.
 */
export function isTaskAutoArchivable(task, rule, ctx) {
    if (!task || !rule) return false;
    if (task.archivedAt) return false;
    if (task.isTemplate) return false;
    if (task.status !== 'done') return false;
    if (rule.includeProjectTasks !== true && task.projectId != null && task.projectId !== '') return false;

    if (rule.mode === 'done-age') {
        const thresholdDays = parseInt(rule.thresholdDays, 10);
        if (!Number.isFinite(thresholdDays) || thresholdDays < 1) return false;
        const doneSince = getDoneSinceMs(task);
        if (doneSince == null) return false;
        const nowMs = ctx && Number.isFinite(ctx.nowMs) ? ctx.nowMs : Date.now();
        const ageDays = (nowMs - doneSince) / 86400000;
        return ageDays >= thresholdDays;
    }
    // on-date / periodic archive every Done task on their run day.
    return true;
}

/**
 * Collect the tasks an auto-archive rule should archive right now.
 * ctx = { todayISO, nowMs }. Returns [] for on-date/periodic rules
 * that are not due today.
 */
export function selectTasksForAutoArchive(rule, tasks, ctx) {
    if (!rule || !rule.active || !Array.isArray(tasks)) return [];
    const context = {
        todayISO: (ctx && ctx.todayISO) || toISODate(new Date()),
        nowMs: ctx && Number.isFinite(ctx.nowMs) ? ctx.nowMs : Date.now(),
    };
    if ((rule.mode === 'on-date' || rule.mode === 'periodic')
        && !archiveRuleShouldRun(rule, context.todayISO)) {
        return [];
    }
    return tasks.filter((task) => isTaskAutoArchivable(task, rule, context));
}

/**
 * Advance a rule after it has run on todayISO (immutable).
 * on-date deactivates; periodic reschedules; done-age just records lastRunDate.
 */
export function advanceArchiveRule(rule, todayISO) {
    if (!rule) return rule;
    if (rule.mode === 'on-date') {
        return { ...rule, active: false, lastRunDate: todayISO };
    }
    if (rule.mode === 'periodic') {
        const opts = { daysOfWeek: rule.daysOfWeek, dayOfMonth: rule.dayOfMonth };
        const next = getNextArchiveRunDate(rule.pattern, todayISO, opts);
        return { ...rule, nextRunDate: next || rule.nextRunDate, lastRunDate: todayISO };
    }
    return { ...rule, lastRunDate: todayISO };
}

/**
 * Human-readable summary of an auto-archive rule (English; caller may localize).
 */
export function describeArchiveRule(rule) {
    if (!rule) return '';
    switch (rule.mode) {
        case 'done-age': {
            const n = rule.thresholdDays;
            return `Archive Done tasks after ${n} day${n === 1 ? '' : 's'}`;
        }
        case 'on-date':
            return `Archive Done tasks on ${rule.archiveDate}`;
        case 'periodic': {
            const label = { daily: 'Daily', weekly: 'Weekly', monthly: 'Monthly' }[rule.pattern] || rule.pattern;
            return `${label} cleanup of Done tasks`;
        }
        default:
            return '';
    }
}

export const AUTOMATION_CONSTANTS = {
    MAX_OCCURRENCES,
    VALID_PATTERNS,
    VALID_ARCHIVE_MODES,
    VALID_ARCHIVE_PATTERNS,
    MAX_THRESHOLD_DAYS,
};
